import { useState } from "react";
import { StickyNote, Trash2 } from "lucide-react";
import { AdminAPI } from "@shared/types/api";
import {
  useCreateUserNote,
  useDeleteUserNote,
} from "../api/useAdminUsers";

/**
 * Staff-notes block shared by the matching popover and the user detail page:
 * existing notes with delete, plus an inline add form. Mutations invalidate
 * the admin event + user-notes queries, so whichever surface fed the notes in
 * refreshes on its own. Internal only — the user never sees these notes.
 */
export function UserNotesSection({
  userId,
  notes,
  showHeader = true,
  collapsedWhenEmpty = false,
}: {
  userId: string;
  notes: AdminAPI.UserNote[];
  /** Hide the "Staff Notes" heading when the surrounding UI already has one. */
  showHeader?: boolean;
  /**
   * With no notes, render only a small "Add note" button until clicked —
   * for tight surfaces like the matching popover where an empty notes block
   * would be noise.
   */
  collapsedWhenEmpty?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [expanded, setExpanded] = useState(false);
  const createNote = useCreateUserNote();
  const deleteNote = useDeleteUserNote();

  const submit = () => {
    const note = draft.trim();
    if (!note || createNote.isPending) return;
    createNote.mutate({ userId, note }, { onSuccess: () => setDraft("") });
  };

  if (collapsedWhenEmpty && notes.length === 0 && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="flex items-center gap-1 text-[11px] text-base-content/50 hover:text-amber-500 transition-colors"
        title="Add an internal staff note — the user never sees these"
      >
        <StickyNote className="w-3 h-3" />
        Add note
      </button>
    );
  }

  return (
    <div>
      {showHeader && (
        <div className="text-[13px] font-bold mb-2 flex items-center gap-1.5">
          <StickyNote className="w-3.5 h-3.5 text-amber-500" />
          Staff Notes
          <span
            className="text-[10px] font-normal text-base-content/50"
            title="Internal only — the user never sees these"
          >
            (internal)
          </span>
        </div>
      )}
      {notes.length > 0 && (
        <div className="flex flex-col gap-1.5 mb-2">
          {notes.map((n) => (
            <div
              key={n.id}
              className="group flex items-start gap-1.5 text-[11px] bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/50 rounded px-2 py-1.5"
            >
              <div className="flex-1 min-w-0">
                <p className="text-base-content whitespace-pre-wrap break-words">
                  {n.note}
                </p>
                <p className="text-[10px] text-base-content/50 mt-0.5">
                  {n.createdBy} ·{" "}
                  {new Date(n.createdAt).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </p>
              </div>
              <button
                onClick={() => deleteNote.mutate(n.id)}
                disabled={deleteNote.isPending}
                className="p-0.5 rounded text-base-content/30 hover:text-error transition-colors shrink-0"
                title="Delete note"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-end gap-1.5">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder="Add a note…"
          autoFocus={expanded}
          rows={1}
          className="textarea textarea-bordered textarea-xs flex-1 min-h-[28px] text-[11px] leading-snug resize-none"
        />
        <button
          onClick={submit}
          disabled={!draft.trim() || createNote.isPending}
          className="btn btn-xs btn-primary"
        >
          Add
        </button>
      </div>
    </div>
  );
}
